import React, { useEffect, useState } from 'react';
import { Typography, Stack, Modal, Box, Card, IconButton, TextField, Grid, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import back2 from '../../../icons/back2.svg';
import close2 from '../../../icons/close2.svg';
import { LoadingButton } from '@mui/lab';
import ThumbUpIcon from '../../../icons/ThumbUpIcon.svg';
import ThumbUpActiveIcon from '../../../icons/ThumbUpActiveIcon.svg';
import ThumbUpInactiveIcon from '../../../icons/ThumbUpInactiveIcon.svg';
import ThumbDownIcon from '../../../icons/ThumbDownIcon.svg';
import ThumbDownActiveIcon from '../../../icons/ThumbDownActiveIcon.svg';
import ThumbDownInactiveIcon from '../../../icons/ThumbDownInactiveIcon.svg';
import UncheckedIcon from '../../../icons/UncheckedIcon.svg';
import CheckedIcon from '../../../icons/CheckedIcon.svg';
import { changeApplicationStatus, selectApplicationsState } from '../../../resui/applications/sagas/pm/applicationsSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
  AssignedConfigsDto,
  ApplicationStatus,
  ApplicationSummaryDto,
  ChangeApplicationStatusDto,
  ChangeApplicationStatusEnum,
  ApplicationAcceptanceTermV2Dto,
  ApplicationAcceptanceTermV2DtoTypeEnum,
} from '@inosio/payments-sdk';
import MaskedInput from '../../../shared/MaskedInput';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import ApplicantNotificationModal from './ApplicantNotificationModal';
import { InputField } from '../../../components/InputController';
import DeleteIcon from '@mui/icons-material/Delete';

export interface AcceptanceTermsLineItem {
  Description: string;
  Amount: string;
}

interface AcceptanceTermErrors extends AcceptanceTermsLineItem {}

const useStyles = makeStyles((theme) => ({
  bodyText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontSize: '18px !important',
    color: '#000000 !important',
  },
  globalError: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: '30px',
    borderTop: '2px  solid #DB3939',
    borderRadius: '24px',
    padding: '12px 20px',
    width: '340px',
    height: '49px',
  },
}));

export enum DecisionModalStates {
  DecisionApp = 'Decision the Application',
  AcceptanceTerms = 'Define Terms of Acceptance',
  DenialReason = 'Adverse Action Reason(s)',
  CustomDenialReason = 'Describe Denial Reason',
}

enum Decisions {
  Approve = 'Approve',
  Deny = 'Deny',
}

enum DenialReasonsEnum {
  RentIncomeRatio = 'Rent : Income Ratio',
  CriminalHistory = 'Criminal History',
  PastTenantPerformance = 'Past Tenant Performance',
  CreditHistory = 'Credit History',
  EvictionFilings = 'Eviction Filings',
  UnitIsNoLongerAvailable = 'Unit Is No Longer Available',
  AnotherApplicantWasSelected = 'Another Applicant Was Selected',
  ApplicantWithdrewApplication = 'Applicant Withdrew Application',
  Other = 'Other:',
}

const DenialReasonsObj = [
  {
    label: 'Rent : Income Ratio',
    description: 'Your rent to income ratio is not adequate.',
  },
  {
    label: 'Criminal History',
    description: 'Criminal history.',
  },
  {
    label: 'Past Tenant Performance',
    description: 'Past tenant performance.',
  },
  {
    label: 'Credit History',
    description: 'Credit history does not meet company requirements.',
  },
  {
    label: 'Eviction Filings',
    description: 'Eviction record(s) does not meet company requirements.',
  },
  {
    label: 'Unit Is No Longer Available',
    description: 'Unit is no longer available.',
  },
  {
    label: 'Another Applicant Was Selected',
    description: 'Another applicant was selected.',
  },
  {
    label: 'Applicant Withdrew Application',
    description: 'Applicant withdrew application.',
  },
];

export interface DecisionApplicationModalProps {
  open: boolean;
  onClose: () => void;
  application: ApplicationSummaryDto | undefined;
  propertyGroupUuid: string;
  initialModalState?: DecisionModalStates; //depending on the scenario we might need to have a different modal state initially
  acceptanceTerms?: ApplicationAcceptanceTermV2Dto[];
}

const DecisionApplicationModal = (props: DecisionApplicationModalProps) => {
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '16px',
    height: '523px',
    pointerEvents: 'auto',
    width: '98%',
    maxWidth: '502px',
    borderRadius: '24px',
    outline: 'none',
  };
  const classes = useStyles();
  const dispatch = useDispatch();
  const { allPropertyGroupsAssignedConfigs } = useSelector(selectPropertyGroupDetailsState);
  const propGroupConfigs = allPropertyGroupsAssignedConfigs?.find((pgc) => pgc.Uuid === props.propertyGroupUuid) || ({} as AssignedConfigsDto);
  //Actual details error
  const initialValues: AcceptanceTermsLineItem[] = [
    {
      Description: 'Rent Amount',
      Amount: '0', //default to zero
    },
  ];
  //Array for tracking their validation
  const initialErrors: AcceptanceTermErrors[] = [
    {
      Description: '',
      Amount: '',
    },
  ];

  let showApplicationDecisionNotificationOption = false;
  if (propGroupConfigs) {
    showApplicationDecisionNotificationOption = !!propGroupConfigs.ShowApplicationDecisionNotificationOption;
  }
  const { isChangingApplicationStatusSuccess, isChangingApplicationStatusFailed, changeApplicationStatusResponse } = useSelector(selectApplicationsState);

  const [modalState, setModalState] = useState<DecisionModalStates>(props.initialModalState || DecisionModalStates.DecisionApp);
  const [decision, setDecision] = useState<Decisions | null>(null);
  const [leaseSignedBy, setLeaseSignedBy] = useState<Dayjs | null>(null);
  const [denialReasons, setDenialReasons] = useState<DenialReasonsEnum[]>([]);
  const [customDenialReason, setCustomDenailReason] = useState('');
  const [openApplicantNotificationModal, setOpenApplicantNotificationModal] = useState(false);
  const [formValues, setFormValues] = useState(initialValues);
  const [fieldErrors, setFieldErrors] = useState(initialErrors);
  const [skippedAcceptanceTerms, setSkippedAcceptanceTerms] = useState(false);
  const [areAcceptanceTermsValid, setAreAcceptanceTermsValid] = useState(false);

  useEffect(() => {
    //if we are decisioning an already approved app again, then populate the existing acceptance terms
    const acceptanceLineItems: AcceptanceTermsLineItem[] = [];
    const acceptanceLineItemErrors: AcceptanceTermErrors[] = [];
    if (!!props.acceptanceTerms?.length) {
      props.acceptanceTerms.forEach((term) => {
        if (term.Type === 'USD') {
          const termLabel = term.Label;
          const termValue = term.Value;
          acceptanceLineItems.push({
            Description: termLabel,
            Amount: termValue,
          } as AcceptanceTermsLineItem);
          acceptanceLineItemErrors.push({
            Description: '',
            Amount: '',
          });
        } else if (term.Type === 'DATE') {
          setLeaseSignedBy(dayjs(term.Value));
        }
      });
      setFormValues(acceptanceLineItems);
      setFieldErrors(acceptanceLineItemErrors);
    }
  }, []);

  //handlers
  let handleChange = (i: number, e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let newFormValues = [...formValues];
    newFormValues[i][e.target.name as keyof AcceptanceTermsLineItem] = e.target.value;
    setFormValues(newFormValues);
  };

  let handleBlur = (i: number, e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let newfieldErrors = [...fieldErrors];
    if (formValues[i][e.target.name as keyof AcceptanceTermsLineItem].length === 0) {
      newfieldErrors[i][e.target.name as keyof AcceptanceTermsLineItem] = '*Required';
      setFieldErrors(newfieldErrors);
    } else {
      newfieldErrors[i][e.target.name as keyof AcceptanceTermsLineItem] = '';
      setFieldErrors(newfieldErrors);
    }
  };

  let addFormFields = () => {
    setFormValues([...formValues, { Description: '', Amount: '0' }]); //default the amount to zero
    setFieldErrors([...fieldErrors, { Description: '', Amount: '' }]);
  };

  let removeFormFields = (i: number) => {
    let newFormValues = [...formValues];
    newFormValues.splice(i, 1);
    setFormValues(newFormValues);

    let newErrorValues = [...fieldErrors];
    newErrorValues.splice(i, 1);
    setFieldErrors(newErrorValues);
  };

  const goToPrevState = () => {
    if (modalState === DecisionModalStates.AcceptanceTerms || modalState === DecisionModalStates.DenialReason) {
      setModalState(DecisionModalStates.DecisionApp);
    } else if (modalState === DecisionModalStates.CustomDenialReason) {
      setModalState(DecisionModalStates.DenialReason);
    }
  };

  const [actionHandled, setActionHandled] = useState(false);
  const handleDecisionApplication = (changeAppStatusDto: ChangeApplicationStatusDto) => {
    dispatch({
      type: changeApplicationStatus.type,
      payload: {
        applicationUuid: props.application?.Uuid,
        changeAppStatusDto,
      },
    });

    setIsSpinnerModalOpen(true);
    setActionHandled(true);
  };

  const getDenialReasons = () => {
    return denialReasons.map((dr) => {
      const dro = DenialReasonsObj.find((dro) => dro.label === dr);
      if (dro) return dro.description;
      return customDenialReason;
    });
  };

  const handleContinue = () => {
    if (modalState === DecisionModalStates.DecisionApp) {
      if (decision === Decisions.Approve) {
        setModalState(DecisionModalStates.AcceptanceTerms);
      } else if (decision === Decisions.Deny) {
        setModalState(DecisionModalStates.DenialReason);
      }
    } else if (modalState === DecisionModalStates.AcceptanceTerms) {
      const acceptanceTerms: ApplicationAcceptanceTermV2Dto[] = [];

      if (!!leaseSignedBy) {
        acceptanceTerms.push({
          Label: 'Lease Signed By',
          Value: leaseSignedBy?.format('MM/DD/YYYY') ?? '',
          Type: ApplicationAcceptanceTermV2DtoTypeEnum.Date,
          IsRequired: true,
        });
      }
      //for now all forma fields are usd fields
      formValues.forEach((field) => {
        //only send the fields that have valid description and amount
        if (!!field.Amount && field.Description) {
          let amount: number = parseFloat(field.Amount);
          let amountTwoPlaces: string = amount.toFixed(2);

          const term: ApplicationAcceptanceTermV2Dto = {
            Label: field.Description,
            Value: amountTwoPlaces,
            Type: ApplicationAcceptanceTermV2DtoTypeEnum.Usd,
            IsRequired: false,
          };
          acceptanceTerms.push(term);
        }
      });
      //decision the application
      handleDecisionApplication({
        Status: ChangeApplicationStatusEnum.Approve,
        AcceptanceTerms: {
          Terms: acceptanceTerms,
        },
      });
    } else if (modalState === DecisionModalStates.DenialReason) {
      if (denialReasons.length) {
        if (denialReasons.some((dr) => dr === DenialReasonsEnum.Other)) {
          setModalState(DecisionModalStates.CustomDenialReason);
        } else {
          //decision the application
          handleDecisionApplication({
            Status: ChangeApplicationStatusEnum.Deny,
            DenialReasons: getDenialReasons(),
          });
        }
      }
    } else if (modalState === DecisionModalStates.CustomDenialReason) {
      //decision the application
      handleDecisionApplication({
        Status: ChangeApplicationStatusEnum.Deny,
        DenialReasons: getDenialReasons(),
      });
    }
  };

  const updateDenialReasons = (reason: DenialReasonsEnum) => {
    if (denialReasons.some((dr) => dr === reason)) {
      setDenialReasons((reasons) => reasons.filter((r) => r !== reason));
    } else {
      setDenialReasons((reasons) => [...reasons, reason]);
    }
  };

  const [disableContinue, setDisableContinue] = useState(true);
  useEffect(() => {
    if (modalState === null) {
      setDisableContinue(true);
    } else {
      if (modalState === DecisionModalStates.DecisionApp) {
        if (decision === null) {
          setDisableContinue(true);
        } else {
          setDisableContinue(false);
        }
      } else if (modalState === DecisionModalStates.AcceptanceTerms) {
        if (leaseSignedBy === null || !areAcceptanceTermsValid) {
          setDisableContinue(true);
        } else if (!!leaseSignedBy && !/\d{1,2}\/\d{1,2}\/\d{4}/.test(leaseSignedBy?.format('DD/MM/YYYY'))) {
          setDisableContinue(true);
        } else {
          setDisableContinue(false);
        }
      } else if (modalState === DecisionModalStates.DenialReason) {
        if (denialReasons.length) {
          setDisableContinue(false);
        } else {
          setDisableContinue(true);
        }
      } else if (modalState === DecisionModalStates.CustomDenialReason) {
        if (customDenialReason.length) {
          setDisableContinue(false);
        } else {
          setDisableContinue(true);
        }
      }
    }
  }, [modalState, decision, leaseSignedBy, denialReasons, customDenialReason, areAcceptanceTermsValid]);

  //spinner modal
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const handleCloseSpinnerModal = React.useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, [dispatch]);

  //handles the denial decision and approval when accpetance terms are not skipped
  useEffect(() => {
    if (actionHandled && !skippedAcceptanceTerms) {
      if (isChangingApplicationStatusSuccess) {
        handleClose();
        //open the notification modal if showApplicationDecisionNotificationOption enabled
        if (showApplicationDecisionNotificationOption) setOpenApplicantNotificationModal(true);

        setSpinnerStatus('stop');
      } else if (isChangingApplicationStatusFailed) {
        setIsSpinnerModalOpen(false);
      }
    }
  }, [actionHandled, isChangingApplicationStatusSuccess, isChangingApplicationStatusFailed]);

  //handles the approval decision when acceptance terms skipped
  useEffect(() => {
    if (actionHandled && skippedAcceptanceTerms) {
      if (isChangingApplicationStatusSuccess) {
        handleClose();

        setSpinnerStatus('stop');
      } else if (isChangingApplicationStatusFailed) {
        setIsSpinnerModalOpen(false);
      }
    }
  }, [actionHandled, isChangingApplicationStatusSuccess, isChangingApplicationStatusFailed]);

  const handleClose = () => {
    if (!props.initialModalState) {
      setModalState(DecisionModalStates.DecisionApp);
    }
    setDecision(null);
    setLeaseSignedBy(null);
    setDenialReasons([]);
    setCustomDenailReason('');
    setActionHandled(false);
    setDisableContinue(true);
    setIsSpinnerModalOpen(false);
    setSpinnerStatus(null);

    if (!props.acceptanceTerms) {
      setLeaseSignedBy(null);
      setFormValues(initialValues);
    } //reset only if there are no existing acceptance terms
    props.onClose();
  };

  const determinButtonTextToShow = (modalState: DecisionModalStates) => {
    switch (modalState) {
      case DecisionModalStates.DecisionApp:
        return 'Continue';
      case DecisionModalStates.AcceptanceTerms:
      case DecisionModalStates.DenialReason:
      case DecisionModalStates.CustomDenialReason:
        return 'Save';
      default:
        break;
    }
  };

  const handleSkipAcceptanceTerms = () => {
    setSkippedAcceptanceTerms(true);
    handleDecisionApplication({
      Status: ChangeApplicationStatusEnum.Approve,
    });
  };

  //Making sure no acceptance field description is empty
  useEffect(() => {
    formValues.forEach((field) => {
      if (field.Description.length === 0) {
        setAreAcceptanceTermsValid(false);
      } else {
        setAreAcceptanceTermsValid(true);
      }
    });
  }, [formValues]);

  return (
    <div>
      <Modal
        open={props.open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        BackdropProps={{
          invisible: false,
        }}
      >
        <Card sx={style}>
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ position: 'relative', mt: 1 }}>
            {modalState !== DecisionModalStates.DecisionApp && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="go to prev"
                onClick={goToPrevState}
                sx={{
                  position: 'absolute',
                  left: 0,
                }}
              >
                <img src={back2} alt="back" />
              </IconButton>
            )}
            <Typography
              sx={{
                fontFamily: '"Work Sans",Sans serif',
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '22px',
                color: '#14142B',
                marginLeft: '0 !important',
              }}
            >
              {modalState}
            </Typography>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Close modal"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 0,
              }}
            >
              <img src={close2} alt="close" />
            </IconButton>
          </Stack>

          {modalState === DecisionModalStates.DecisionApp ? (
            <React.Fragment>
              <Typography className={classes.bodyText} sx={{ textAlign: 'center', mt: 4 }}>
                Select the decision you've made.
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ cursor: 'pointer' }} onClick={() => setDecision(Decisions.Approve)}>
                    <img src={decision === Decisions.Approve ? ThumbUpActiveIcon : decision === Decisions.Deny ? ThumbUpInactiveIcon : ThumbUpIcon} alt="approve" />
                  </Box>
                  <Typography className={classes.bodyText} sx={{ textAlign: 'center', mt: 3 }}>
                    Approve
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ cursor: 'pointer' }} onClick={() => setDecision(Decisions.Deny)}>
                    <img src={decision === Decisions.Deny ? ThumbDownActiveIcon : decision === Decisions.Approve ? ThumbDownInactiveIcon : ThumbDownIcon} alt="approve" />
                  </Box>
                  <Typography className={classes.bodyText} sx={{ textAlign: 'center', mt: 3 }}>
                    Deny
                  </Typography>
                </Box>
              </Box>
            </React.Fragment>
          ) : modalState === DecisionModalStates.AcceptanceTerms ? (
            <Box sx={{ my: 4 }}>
              <Grid container>
                <Grid item xs={1}></Grid>
                <Grid item xs={6.5}>
                  <Typography className={classes.bodyText}>Lease Signed By:</Typography>
                </Grid>
                <Grid item xs={4.5}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={leaseSignedBy}
                      onChange={(dayjsValue) => setLeaseSignedBy(dayjsValue)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: 'white',
                          height: '33px',
                          borderRadius: '24px',
                          border: 'none',
                          color: '#000',
                          marginLeft: 2,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              <Box
                sx={{
                  maxHeight: '320px',
                  marginTop: 2,
                  overflow: 'auto',
                  '&::-webkit-scrollbar': {
                    width: 7,
                  },
                  '&::-webkit-scrollbar-track': {
                    boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                    borderRadius: '24px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'darkgrey',
                    borderRadius: '24px',
                  },
                }}
              >
                {formValues.map((element, index) => {
                  return (
                    <Grid container key={index}>
                      <Grid item xs={1}>
                        {index !== 0 ? (
                          <IconButton onClick={() => removeFormFields(index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <></>
                        )}
                      </Grid>
                      <Grid item xs={6.5}>
                        <InputField
                          name="Description"
                          placeholder="Description"
                          isboxShadow={true}
                          isSlim={true}
                          isError={false}
                          onChange={(e) => handleChange(index, e)}
                          value={element.Description || ''}
                          errors={fieldErrors[index]['Description']}
                          onBlur={(e: any) => handleBlur(index, e)}
                          touched={true}
                          maxCharLength={50}
                        />
                      </Grid>
                      <Grid item xs={4.5}>
                        <MaskedInput
                          onBlur={(e: any) => handleBlur(index, e)}
                          label=""
                          name="Amount"
                          values={element.Amount || '0'}
                          handleChange={(e) => handleChange(index, e)}
                          placeholder="$0.00"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '25px',
                              height: '33px',
                              marginLeft: 2,
                            },
                            '& .MuiOutlinedInput-root>input': {
                              textAlign: 'center',
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  );
                })}

                <Grid container justifyContent={'center'}>
                  <Button
                    variant="outlined"
                    sx={{
                      borderRadius: '24px',
                      textTransform: 'none',
                      color: '#39A2DB',
                      transition: 'background 0.55s, color 0.55s',
                      borderColor: '#39A2DB',
                      '&:hover': {
                        backgroundColor: '#39A2DB',
                        color: 'white',
                        borderColor: '#39A2DB',
                      },
                    }}
                    onClick={addFormFields}
                  >
                    Add another item
                  </Button>
                </Grid>
              </Box>
            </Box>
          ) : modalState === DecisionModalStates.DenialReason ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box>
                <Typography className={classes.bodyText} sx={{ mt: 4 }}>
                  Select All That Apply:
                </Typography>

                {(Object.keys(DenialReasonsEnum) as (keyof typeof DenialReasonsEnum)[]).map((reason, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mt: 1, cursor: 'pointer' }} onClick={() => updateDenialReasons(DenialReasonsEnum[reason])}>
                    <Box sx={{ width: 20, height: 17 }}>
                      <img src={denialReasons.some((dr) => dr === DenialReasonsEnum[reason]) ? CheckedIcon : UncheckedIcon} alt="checked" />
                    </Box>
                    <Typography className={classes.bodyText} sx={{ ml: 4 }}>
                      {DenialReasonsEnum[reason]}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : modalState === DecisionModalStates.CustomDenialReason ? (
            <Box>
              <Typography className={classes.bodyText} sx={{ mt: 4, mx: 2, fontSize: '16px !important' }}>
                Please describe what the denial reason is. Please ensure you follow{' '}
                <a
                  href="https://www.hud.gov/program_offices/fair_housing_equal_opp/fair_housing_act_overview#_What_Is_Prohibited"
                  target="blank"
                  style={{ color: '#172B85', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  Fair Housing guidelines.
                </a>
              </Typography>

              <Box
                sx={{
                  '& fieldset': {
                    borderRadius: '24px',
                    borderColor: 'white',
                    height: '232px',
                  },
                  mt: 3,
                }}
              >
                <TextField
                  multiline
                  value={customDenialReason}
                  onChange={(e) => {
                    setCustomDenailReason(e.target.value);
                  }}
                  rows={8}
                  sx={{ width: '100%', backgroundColor: '#F1F0F5', borderRadius: '24px', height: '228px' }}
                  placeholder={'Rent to income ratio does not meet our policies.'}
                  inputProps={{ maxLength: 150 }}
                />
              </Box>
              <Typography className={classes.bodyText} sx={{ mt: 0.5, mx: 1, fontSize: '16px !important' }}>
                {`${customDenialReason.length}/150 characters`}
              </Typography>
            </Box>
          ) : (
            <></>
          )}

          <Box sx={{ position: 'absolute', bottom: 16, right: 16 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton
                variant="contained"
                loading={false}
                disabled={disableContinue}
                style={{
                  textTransform: 'none',
                  fontFamily: "'Open Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 700,
                  backgroundColor: disableContinue ? '#D3D3D3' : '#39A2DB',
                  borderRadius: '24px',
                  width: '115px',
                }}
                onClick={handleContinue}
              >
                {determinButtonTextToShow(modalState)}
              </LoadingButton>
            </Box>
          </Box>
          {modalState === DecisionModalStates.AcceptanceTerms && props.application?.Status === ApplicationStatus.Submitted && (
            <Box sx={{ position: 'absolute', bottom: 16, left: 16 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  sx={{
                    borderRadius: '24px',
                    textTransform: 'none',
                    color: '#39A2DB',
                    transition: 'background 0.55s, color 0.55s',
                    width: '115px',
                    borderColor: '#39A2DB',
                    '&:hover': {
                      backgroundColor: '#39A2DB',
                      color: 'white',
                      borderColor: '#39A2DB',
                    },
                  }}
                  onClick={handleSkipAcceptanceTerms}
                >
                  Skip
                </Button>
              </Box>
            </Box>
          )}
        </Card>
      </Modal>

      {isSpinnerModalOpen && (
        <SpinnerModal
          width="502px"
          height="523px"
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => {
            setIsSpinnerModalOpen(false);
            setSpinnerStatus(null);
          }}
        />
      )}
      <ApplicantNotificationModal
        open={openApplicantNotificationModal}
        onClose={() => {
          setOpenApplicantNotificationModal(false);
        }}
        application={props.application}
        applicationDecision={
          changeApplicationStatusResponse?.ApplicationStatus === ApplicationStatus.Rejected
            ? ApplicationStatus.Rejected
            : changeApplicationStatusResponse?.ApplicationStatus === ApplicationStatus.Approved
            ? ApplicationStatus.Approved
            : null
        }
      />
    </div>
  );
};
export default DecisionApplicationModal;
